import { LOCATION_CHANGE, RouterAction } from "connected-react-router"
import { Reducer, Action } from "redux"
import { AuthUserType, InstanceAPIType } from "../../../Actions/Types"
import {
    APIAppInstanceModel,
    AppInstanceListParams,
    JobWithFeedbackStats
} from "../../../Models/API/AppInstanceModel"
import {
    getInitialData,
    mergeRecords,
    RemoteData,
    setPage,
    setPagesToStale
} from "../../../Models/Client/RemoteData"
import { Result } from "../../../Models/Utility/Result"
import { AuthAction } from "../../Auth"
import {
    hasValue,
    FetchState,
    getValue
} from "../../../Models/Client/FetchState"
import { BatchListModel } from "../../../Models/API/BatchListModel"
import { updateAt } from "../../../Helpers/Functions/reducer"
import { APIAppPlan } from "../../../Models/API/APIAppPlans"
import { APIAppMeasurerConfiguration } from "../../../Models/API/APIAppMeasurerConfiguration"
import { APIJobModel, APIJobListParams } from "../../../Models/API/JobModel"
import { Map } from "immutable"
import { APIAppInstanceMetricModel } from "../../../Models/API/AppMetricModel"
import { APIAppAsyncOperationModel } from "../../../Models/API/APIAppAsyncOperationModel"
import { matchPath } from "react-router"
import { abstractRoutePaths } from "../../../Routes"
import { defaultAppSettings } from "../../../Models/API/AppSettings"

export type APIAppInstanceState = APIAppInstanceModel & {
    batchProcessCount: number
    jobWithFeedbackStats?: JobWithFeedbackStats
    isSubmittingNewJob?: boolean
}

export interface AppInstanceState {
    apps: RemoteData<APIAppInstanceState, AppInstanceListParams, string>
    tags: {
        [appID: string]:
            | Map<string, RemoteData<string, { keyword: string }, string>>
            | undefined
    }
    plansMetadata: { [appID: string]: FetchState<APIAppPlan[]> }
    measurerConfiguration: {
        [appID: string]: FetchState<APIAppMeasurerConfiguration>
    }
    metrics: {
        [appID: string]: FetchState<APIAppInstanceMetricModel[]>
    }
    asyncOperation: {
        [appID: string]: FetchState<APIAppAsyncOperationModel[]>
    }
    totalJobCount: {
        [appID: string]: number
    }
}

interface AppInstanceSetListAction {
    type: InstanceAPIType.setList
    meta: {
        total: number
        page: number
        params: AppInstanceListParams
        totalArchived: number
    }
    payload: APIAppInstanceModel[]
}

interface AppInstanceClearListAction {
    type: InstanceAPIType.clearList
}

interface AppInstanceAddInstanceAction {
    type: InstanceAPIType.addInstance
    payload: APIAppInstanceModel
}

interface AppInstanceAddErrorAction {
    type: InstanceAPIType.addError
    meta: {
        appID: string
    }
    payload: string
}

interface AppInstanceSetProcessingBatchList {
    type: InstanceAPIType.setProcessingBatchList
    meta: {
        appID: string
    }
    payload: BatchListModel
}

interface AppInstanceStartBatchSubmit {
    type: InstanceAPIType.startBatchSubmit
    meta: {
        appID: string
    }
    payload: number // number of jobs to be added, can be zero (when relabeling)
}

interface AppInstanceProgressBatchSubmit {
    type: InstanceAPIType.progressBatchSubmit
    meta: {
        appID: string
        page: number
        total: number
        filteredTotal: number
        params: APIJobListParams
    }
    payload: {
        remainingCount: number
        jobs: APIJobModel[]
    }
}

interface AppInstanceSetTagList {
    type: InstanceAPIType.setTagList
    meta: {
        appID: string
        key: string
        keyword: string
    }
    payload: string[]
}

interface AppInstanceSetPlansMetadata {
    type: InstanceAPIType.setPlansMetadata
    meta: {
        appID: string
    }
    payload: APIAppPlan[]
}

interface AppInstanceAddJobWithFeedbackStatsAction {
    type: InstanceAPIType.addJobWithFeedbackStats
    meta: {
        appID: string
    }
    payload: JobWithFeedbackStats
}

interface AppInstanceResetUnreadMessagesCountAction {
    type: InstanceAPIType.resetUnreadMessagesCount
    meta: {
        appID: string
    }
}

interface AppInstanceExtraAction {
    type:
        | InstanceAPIType.setView
        | InstanceAPIType.baselineData_setList
        | InstanceAPIType.customizeMetrics_latestFetched
        | InstanceAPIType.customizeMetrics_chooseMetricToOptimize
        | InstanceAPIType.customizeMetrics_chooseQualityMetricToOptimize
}

interface AppInstanceSetMeasurerConfiguration
    extends Action<InstanceAPIType.setMeasurerConfiguration> {
    payload: APIAppMeasurerConfiguration
    meta: {
        appID: string
    }
}

interface AppInstanceMetrics {
    type: InstanceAPIType.setMetricsByAppID
    payload: APIAppInstanceMetricModel[]
    meta: {
        appID: string
    }
}

interface AppInstanceSetAsyncOperationsAction {
    type: InstanceAPIType.setAsyncOperations
    payload: APIAppAsyncOperationModel[]
    meta: {
        appID: string
    }
}

interface AppInstanceTotalJobCountAction
    extends Action<InstanceAPIType.setTotalJobCount> {
    payload: number
    meta: {
        appID: string
    }
}

export type AppInstanceAction =
    | AppInstanceSetListAction
    | AppInstanceClearListAction
    | AppInstanceExtraAction
    | AppInstanceAddInstanceAction
    | AppInstanceAddErrorAction
    | AppInstanceSetProcessingBatchList
    | AppInstanceStartBatchSubmit
    | AppInstanceProgressBatchSubmit
    | AppInstanceSetTagList
    | AppInstanceSetPlansMetadata
    | AppInstanceSetMeasurerConfiguration
    | AppInstanceAddJobWithFeedbackStatsAction
    | AppInstanceResetUnreadMessagesCountAction
    | AppInstanceMetrics
    | AppInstanceSetAsyncOperationsAction
    | AppInstanceTotalJobCountAction

const initialState: AppInstanceState = {
    apps: getInitialData(),
    tags: {},
    plansMetadata: {},
    measurerConfiguration: {},
    metrics: {},
    asyncOperation: {},
    totalJobCount: {}
}

const getId = (model: APIAppInstanceModel) => model.uuid

export const AppInstanceReducer: Reducer<
    AppInstanceState,
    AppInstanceAction | AuthAction | RouterAction
> = (state = initialState, action): AppInstanceState => {
    switch (action.type) {
        case LOCATION_CHANGE:
            const shouldResetTotalJobCount =
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.jobsDetail
                }) === null &&
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.appDetail
                }) === null &&
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.appAddData
                }) === null

            return {
                ...state,
                apps: setPagesToStale(state.apps, 0),
                totalJobCount: shouldResetTotalJobCount
                    ? {}
                    : state.totalJobCount
            }
        case InstanceAPIType.setList:
            return {
                ...state,
                apps: setPage(
                    state.apps,
                    action.meta.params,
                    action.payload.map(instance => ({
                        ...instance,
                        tags: [],
                        batchProcessCount: 0
                    })),
                    getId,
                    action.meta.page,
                    action.meta.total,
                    { totalArchived: action.meta.totalArchived }
                )
            }

        case InstanceAPIType.addInstance:
            const currentInstance = state.apps.records.get(action.payload.uuid)
            const newInstanceState: APIAppInstanceState = {
                ...action.payload,
                settings: {
                    ...defaultAppSettings,
                    ...action.payload.settings
                },
                ...(hasValue(currentInstance)
                    ? {
                          batchProcessCount:
                              currentInstance.value.batchProcessCount,
                          jobWithFeedbackStats:
                              currentInstance.value.jobWithFeedbackStats
                      }
                    : { batchProcessCount: 0 })
            }

            if (
                JSON.stringify(getValue(currentInstance)) ===
                JSON.stringify(newInstanceState)
            ) {
                return state
            }

            return {
                ...state,
                apps: {
                    ...state.apps,
                    records: mergeRecords(
                        state.apps.records,
                        [newInstanceState],
                        getId
                    )
                },
                tags: { ...state.tags, [action.payload.uuid]: undefined }
            }
        case InstanceAPIType.setProcessingBatchList:
        case InstanceAPIType.progressBatchSubmit:
        case InstanceAPIType.startBatchSubmit:
        case InstanceAPIType.addJobWithFeedbackStats:
            const update = (
                record: FetchState<APIAppInstanceState>
            ): FetchState<APIAppInstanceState> => {
                if (!hasValue(record)) {
                    return record
                }

                switch (action.type) {
                    case InstanceAPIType.setProcessingBatchList:
                        if (
                            action.payload.total_jobs ===
                            record.value.batchProcessCount
                        ) {
                            return record
                        }

                        return Result.ok({
                            ...record.value,
                            batchProcessCount: action.payload.total_jobs
                        })
                    case InstanceAPIType.startBatchSubmit:
                        return Result.ok({
                            ...record.value,
                            batchProcessCount: action.payload,
                            isSubmittingNewJob: true
                        })
                    case InstanceAPIType.progressBatchSubmit:
                        return Result.ok({
                            ...record.value,
                            batchProcessCount: action.payload.remainingCount,
                            isSubmittingNewJob:
                                action.payload.remainingCount > 0
                        })
                    case InstanceAPIType.addJobWithFeedbackStats:
                        return Result.ok({
                            ...record.value,
                            jobWithFeedbackStats: action.payload
                        })
                }
            }

            return {
                ...state,
                apps: {
                    ...state.apps,
                    records: state.apps.records.update(
                        action.meta.appID,
                        update
                    )
                }
            }
        case InstanceAPIType.addError:
            if (hasValue(state.apps.records.get(action.meta.appID))) {
                // do not overwrite successful response with error response
                return state
            }

            return {
                ...state,
                apps: {
                    ...state.apps,
                    records: state.apps.records.set(
                        action.meta.appID,
                        Result.error(action.payload)
                    )
                }
            }
        case InstanceAPIType.setTagList:
            return {
                ...state,
                tags: updateAt(state.tags, action.meta.appID, map =>
                    (
                        map ??
                        Map<
                            string,
                            RemoteData<string, { keyword: string }, string>
                        >()
                    ).update(action.meta.key, getInitialData(), remoteData =>
                        setPage(
                            remoteData,
                            { keyword: action.meta.keyword },
                            action.payload,
                            tag => tag,
                            0,
                            action.payload.length
                        )
                    )
                )
            }
        case InstanceAPIType.setPlansMetadata:
            return {
                ...state,
                plansMetadata: updateAt(
                    state.plansMetadata,
                    action.meta.appID,
                    () => Result.ok(action.payload)
                )
            }
        case InstanceAPIType.setMeasurerConfiguration:
            return {
                ...state,
                measurerConfiguration: {
                    ...state.measurerConfiguration,
                    [action.meta.appID]: Result.ok(action.payload)
                }
            }

        case InstanceAPIType.resetUnreadMessagesCount:
            return {
                ...state,
                apps: {
                    ...state.apps,
                    records: state.apps.records.update(
                        action.meta.appID,
                        record => {
                            if (
                                hasValue(record) &&
                                record.value.jobWithFeedbackStats
                            ) {
                                return Result.ok({
                                    ...record.value,
                                    jobWithFeedbackStats: {
                                        ...record.value.jobWithFeedbackStats,
                                        totalUnread: 0
                                    }
                                })
                            }
                            return record
                        }
                    )
                }
            }
        case InstanceAPIType.setMetricsByAppID:
            return {
                ...state,
                metrics: updateAt(state.metrics, action.meta.appID, () =>
                    Result.ok(action.payload)
                )
            }
        case InstanceAPIType.setAsyncOperations:
            return {
                ...state,
                asyncOperation: updateAt(
                    state.asyncOperation,
                    action.meta.appID,
                    () => Result.ok(action.payload)
                )
            }
        case InstanceAPIType.setTotalJobCount:
            return {
                ...state,
                totalJobCount: { [action.meta.appID]: action.payload }
            }
        case InstanceAPIType.clearList:
        case AuthUserType.signedOut:
            return initialState
        default:
            return state
    }
}
